import React, { useState, useCallback, useEffect, useRef } from 'react';
import TopologyView, { POLL_INTERVAL_MS } from './TopologyView';
import NodeDetail, { HamburgerMenu } from './NodeDetail';
import RendezvousPanel from './RendezvousPanel';
import NetworksView, { RenameNetworkModal, DecommissionNetworkModal, PublicHelpersModal } from './NetworksView';
import ApprovalsPanel from './ApprovalsPanel';
import AppsPanel from './AppsPanel';
import PermissionsPanel from './PermissionsPanel';
import UsersPanel from './UsersPanel';
import JobsPanel from './JobsPanel';
import { AuthProvider, LoginGate, useAuth } from './AuthContext';
import { useCanI } from './useCanI';

const CLUSTER_ROOT_ITEM = { depth: 'cluster', id: 0 };

// URL ↔ state binding (multi-network slug, browser back/forward support).
//
// URL shapes the app honors:
//   /                            → networks list (canonical landing)
//   /networks                    → networks list
//   /networks/<canonical_name>   → topology tab, filtered to that network
//   /topology                    → topology tab, unfiltered (rare; direct tab click without a network)
//   /rendezvous                  → rendezvous tab
//   /apps, /tasks, /permissions, /users, /jobs → that tab
//
// Optional query: ?node=<id> on a topology URL restores the right-side
// NodeDetail selection.
//
// Networks fetch lives in App so the popstate handler can resolve
// canonical_name → network row without waiting for NetworksView to
// mount. NetworksView still fetches its own copy on mount; the
// duplication is one extra GET on first paint and avoids prop-drilling
// the refresh callback through two extra layers.

// Tabs whose URL is just `/<tab>`. parseUrl and urlFor both read this list.
export const PATH_TABS = ['rendezvous', 'apps', 'tasks', 'permissions', 'users', 'jobs'];

export function parseUrl(networks) {
  const path = window.location.pathname;
  const params = new URLSearchParams(window.location.search);
  const node = params.get('node');

  // /, /networks, /networks/ → networks list
  if (path === '/' || path === '/networks' || path === '/networks/') {
    return { tab: 'networks', network: null, node: null };
  }

  // /networks/<canonical_name> → topology filtered
  const m = path.match(/^\/networks\/([^/]+)\/?$/);
  if (m) {
    const canonical = decodeURIComponent(m[1]);
    const network = (networks || []).find((n) => n.canonical_name === canonical) || null;
    // No ?node= → the network's root, so the node panel is never empty on open.
    const root = network && network.root_node_id ? String(network.root_node_id) : null;
    return { tab: 'topology', network, node: node || root };
  }

  // /topology → topology unfiltered
  if (path === '/topology' || path === '/topology/') {
    return { tab: 'topology', network: null, node };
  }

  const simple = path.match(/^\/([a-z]+)\/?$/);
  if (simple && PATH_TABS.includes(simple[1])) {
    return { tab: simple[1], network: null, node: null };
  }

  // Anything else (the SPA catch-all earlier serves these) → default landing.
  return { tab: 'networks', network: null, node: null };
}

export function urlFor(tab, network, node) {
  if (PATH_TABS.includes(tab)) return `/${tab}`;
  if (tab === 'topology') {
    const base = network ? `/networks/${encodeURIComponent(network.canonical_name)}` : '/topology';
    return node ? `${base}?node=${encodeURIComponent(node)}` : base;
  }
  return '/networks';
}

export default function App() {
  return (
    <AuthProvider>
      <LoginGate>
        <AuthedApp />
      </LoginGate>
    </AuthProvider>
  );
}


function AuthedApp() {
  const { operator, logout } = useAuth();
  // Per-tab permission checks. Each returns {loading, can, reason}.
  // Networks + Topology + Apps + Tasks are always visible to authenticated
  // operators — their data is visibility-filtered to whatever networks
  // the operator has any role assignment within. Rendezvous +
  // Permissions stay gated (admin-only / ultimate-only respectively).
  const canRendezvous = useCanI('read_rendezvous', CLUSTER_ROOT_ITEM);
  // The Permissions tab is gated by `grant_permission` — only the
  // highest-trust operators (those who can manage the permission
  // system itself) see it.
  const canPermissions = useCanI('grant_permission', CLUSTER_ROOT_ITEM);
  // Users tab — chat-user access management (aveq-admin-user-access slug).
  const canUsers = useCanI('manage_user_access', CLUSTER_ROOT_ITEM);
  // Jobs tab — SYSTEM scheduled jobs (admin-scheduled-jobs slug).
  const canJobs = useCanI('manage_jobs', CLUSTER_ROOT_ITEM);
  const [selectedId, setSelectedId] = useState(null);
  // Networks tab lands first — explicit grouping context before
  // operators dive into the topology tree. Clicking a network in
  // NetworksView sets `selectedNetworkRoot` and switches to the
  // topology tab; TopologyView then renders only that network's
  // subtree (via `rootNodeId={selectedNetworkRoot.root_node_id}`).
  // Null = no network selected.
  const [tab, setTab] = useState('networks');
  const [selectedNetworkRoot, setSelectedNetworkRoot] = useState(null);
  // Per-network page (banner) hamburger actions — Edit / Decommission, moved
  // here from the Networks list rows to sit next to the network name.
  const [netRenameTarget, setNetRenameTarget] = useState(null);
  const [netDecommTarget, setNetDecommTarget] = useState(null);
  const [netPublicTarget, setNetPublicTarget] = useState(null);  // declare-public editor
  // App's own copy of the networks list — used to resolve URL paths
  // like /networks/<canonical_name> at mount time + on popstate. The
  // NetworksView component fetches its own copy on mount too (a duplicate
  // GET; cheap, and avoids prop-drilling a shared refresh callback).
  const [networks, setNetworks] = useState(null);

  // Fetched once on mount from GET /api/version. Single-sourced from the
  // baked aveq_link.__version__ (bumped in CLAUDE.md's post-merge ritual) —
  // no separate APP_VERSION env, so the badge can't drift from the services.
  const [version, setVersion] = useState('');
  useEffect(() => {
    fetch('/api/version').then(r => r.ok ? r.json() : null).then(d => {
      if (d && d.version) setVersion(d.version);
    }).catch(() => {});
  }, []);

  // Node ids whose probe round-tripped OK in this session. Used to
  // override the topology's `live` color — root-qbric's view of
  // downstream liveness is unreliable when traffic flows via rendezvous
  // (see backlog/indirect-liveness), so a successful probe is more
  // ground truth than the API's `live` field.
  const [probedOkIds, setProbedOkIds] = useState(() => new Set());
  const markProbedOk = useCallback((nodeId) => setProbedOkIds((prev) => {
    if (prev.has(nodeId)) return prev;
    const next = new Set(prev);
    next.add(nodeId);
    return next;
  }), []);

  // decommission slug: optimistic node-removal. qbric's /admin/topology
  // serves a snapshot it refreshes on its cleanup-interval (~60s), so a
  // freshly-decommissioned node lingers in the 5s topology poll until
  // qbric catches up. Track deleted ids here and let TopologyView filter
  // them client-side; once qbric refreshes, the poll renders fine on its
  // own (filter is a no-op then). A 5-min safety timer drops the id from
  // the hide-set in case something keeps it alive upstream.
  const [removedNodeIds, setRemovedNodeIds] = useState(() => new Set());
  // indirect-liveness slug: last /api/topology fetch time, lifted from
  // TopologyView so the status legend can live in the app-wide bottom bar
  // (its own panel pinned to the bottom of the viewport).
  const [topoLastFetch, setTopoLastFetch] = useState(null);
  const markNodeRemoved = useCallback((nodeId) => {
    if (!nodeId) return;
    setRemovedNodeIds((prev) => {
      if (prev.has(nodeId)) return prev;
      const next = new Set(prev);
      next.add(nodeId);
      return next;
    });
    setTimeout(() => {
      setRemovedNodeIds((prev) => {
        if (!prev.has(nodeId)) return prev;
        const next = new Set(prev);
        next.delete(nodeId);
        return next;
      });
    }, 5 * 60 * 1000);
  }, []);

  // --- URL ↔ state plumbing ---
  //
  // Guard ref prevents the (state-changes-push-URL) effect from firing
  // when the change was JUST received from popstate or the initial
  // URL parse. Without this, popstate would call setState → effect
  // pushes a NEW history entry → back button no longer behaves like
  // back.
  const skipNextPush = useRef(false);

  // Fetch networks once. Re-fetched on visible mutations (NetworksView
  // refresh / rename / Add Network) via the `refreshNetworks` callback
  // below; the duplicate GET on NetworksView mount also happens, harmless.
  const fetchNetworks = useCallback(async () => {
    try {
      const r = await fetch('/api/networks/');
      if (!r.ok) return;
      const j = await r.json();
      setNetworks((j && j.entity) || []);
    } catch (_) {}
  }, []);

  // 1. Initial mount: fetch networks, then apply URL → state.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      await fetchNetworks();
      if (cancelled) return;
      // We pass `networks` via the state setter pattern (fetchNetworks
      // already set it). Read it back via a follow-up render rather
      // than a stale closure. The setNetworks above triggers a
      // re-render; the second useEffect below handles URL→state.
    })();
    return () => { cancelled = true; };
  }, [fetchNetworks]);

  // 2. Once networks are loaded, resolve the initial URL → state.
  // Runs every time `networks` flips from null → array (i.e. once).
  const didInitialUrlParse = useRef(false);
  useEffect(() => {
    if (networks === null || didInitialUrlParse.current) return;
    didInitialUrlParse.current = true;
    const parsed = parseUrl(networks);
    skipNextPush.current = true;
    setTab(parsed.tab);
    setSelectedNetworkRoot(parsed.network);
    setSelectedId(parsed.node);
    // Normalize the URL via replaceState (not pushState) so the FIRST
    // history entry matches what the state will pushState as later.
    // Avoids back-button leaving the operator on a "/index.html"-shaped
    // URL never represented in app state.
    window.history.replaceState(
      { tab: parsed.tab, canonical: parsed.network ? parsed.network.canonical_name : null, node: parsed.node },
      '',
      urlFor(parsed.tab, parsed.network, parsed.node),
    );
  }, [networks]);

  // 3. State change → pushState (unless we just received the change
  // from popstate / initial parse).
  useEffect(() => {
    if (networks === null) return; // wait for initial parse
    if (skipNextPush.current) { skipNextPush.current = false; return; }
    const targetUrl = urlFor(tab, selectedNetworkRoot, selectedId);
    if (window.location.pathname + window.location.search !== targetUrl) {
      window.history.pushState(
        { tab, canonical: selectedNetworkRoot ? selectedNetworkRoot.canonical_name : null, node: selectedId },
        '',
        targetUrl,
      );
    }
  }, [tab, selectedNetworkRoot, selectedId, networks]);

  // 4. Browser back/forward → re-parse URL → setState.
  useEffect(() => {
    const onPop = () => {
      const parsed = parseUrl(networks);
      skipNextPush.current = true;
      setTab(parsed.tab);
      setSelectedNetworkRoot(parsed.network);
      setSelectedId(parsed.node);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [networks]);

  // Selecting a network from the Networks tab — keep state + URL in
  // sync. `setTab('topology')` here triggers the pushState effect.
  const handleSelectNetwork = useCallback((network) => {
    setSelectedNetworkRoot(network);
    setSelectedId(network && network.root_node_id ? String(network.root_node_id) : null);
    setTab('topology');
  }, []);

  // Apps tab row → that node, in its network.
  const handleOpenAppNode = useCallback((app) => {
    const net = (networks || []).find((n) => String(n.id) === String(app.network && app.network.id));
    setSelectedNetworkRoot(net || null);
    setSelectedId(app.node_id);
    setTab('topology');
  }, [networks]);

  // NodeDetail portals the station panel here, just left of the node panel.
  const [stationPanelHost, setStationPanelHost] = useState(null);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      {/* Header */}
      <div style={{ padding: '10px 20px', borderBottom: '1px solid #e0e0e0', background: '#fff', display: 'flex', alignItems: 'center', flex: '0 0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flex: '0 0 auto' }}>
          <img src="/aveqlogo.svg" alt="" style={{ height: '40px' }} />
          <span style={{ fontSize: '1.15rem', textTransform: 'uppercase', letterSpacing: '0.05em', lineHeight: 1 }}>AVE Q</span>
          <strong style={{ fontSize: '1rem', lineHeight: 1 }}>admin</strong>
        </div>
        <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'baseline', gap: 8 }}>
          <Tab label="networks" active={tab === 'networks' || tab === 'topology'}
               onClick={() => { setSelectedNetworkRoot(null); setSelectedId(null); setTab('networks'); }} />
          {canRendezvous.can && (
            <Tab label="rendezvous" active={tab === 'rendezvous'} onClick={() => setTab('rendezvous')} />
          )}
          <Tab label="apps" active={tab === 'apps'} onClick={() => setTab('apps')} />
          <Tab label="tasks" active={tab === 'tasks'} onClick={() => setTab('tasks')} />
          {canPermissions.can && (
            <Tab label="permissions" active={tab === 'permissions'} onClick={() => setTab('permissions')} />
          )}
          {canUsers.can && (
            <Tab label="users" active={tab === 'users'} onClick={() => setTab('users')} />
          )}
          {canJobs.can && (
            <Tab label="jobs" active={tab === 'jobs'} onClick={() => setTab('jobs')} />
          )}
        </div>
        <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'baseline', gap: 12 }}>
          {operator && (
            <span style={{ fontSize: '0.8rem', color: '#555' }}>
              {operator.display_name}
            </span>
          )}
          {operator && (
            <button onClick={logout} style={{
              background: 'none', border: '1px solid #ccc', borderRadius: '3px',
              padding: '2px 8px', fontSize: '0.75rem', color: '#666',
              cursor: 'pointer',
            }}
              title="Sign out">
              sign out
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <div style={{ flex: 1, position: 'relative', overflow: 'auto', background: '#fafafa' }}>
          {tab === 'networks' && <NetworksView onSelectNetwork={handleSelectNetwork} />}
          {tab === 'topology' && (
            <>
              <div style={{
                padding: '6px 20px', background: '#eef5ff', borderBottom: '1px solid #d5e3f5',
                fontSize: '0.8rem',
                // core-network-tiers slug: the topology panel scrolls
                // horizontally (wide SVG); stick the breadcrumb to the
                // viewport-left so its background covers the full visible
                // width instead of cutting off when scrolled right.
                position: 'sticky', left: 0, zIndex: 2,
              }}>
                {/* Breadcrumb — replaces the standalone Topology tab nav.
                    "All networks" jumps back to the Networks list. */}
                <button type="button"
                  onClick={() => { setSelectedNetworkRoot(null); setSelectedId(null); setTab('networks'); }}
                  style={{
                    background: 'none', border: 'none', padding: 0,
                    color: '#3b6bb1', cursor: 'pointer', fontSize: 'inherit',
                    textDecoration: 'underline',
                  }}>
                  All networks
                </button>
                <span style={{ color: '#aab', margin: '0 6px' }}>&gt;</span>
                {selectedNetworkRoot ? (
                  <span style={{ color: '#445', display: 'inline-flex', alignItems: 'center' }}>
                    <strong>{selectedNetworkRoot.display_name}</strong>{' '}
                    <code style={{ color: '#778' }}>({selectedNetworkRoot.canonical_name})</code>
                    {/* Network-level functions — hamburger next to the name,
                        consistent with node/station menus. "Public helpers" is
                        available for ALL networks (incl. the core/System standard-
                        library net, where the scheduler lives); Edit/Decommission
                        stay hidden for core-managed (System) networks. */}
                    <span style={{ marginLeft: 6 }}>
                      <HamburgerMenu items={[
                        { label: 'Public helpers', onClick: () => setNetPublicTarget(selectedNetworkRoot) },
                        ...(selectedNetworkRoot.root_node_type !== 'System' ? [
                          { label: 'Edit network', onClick: () => setNetRenameTarget(selectedNetworkRoot) },
                          { label: 'Decommission network', danger: true, onClick: () => setNetDecommTarget(selectedNetworkRoot) },
                        ] : []),
                      ]} />
                    </span>
                  </span>
                ) : (
                  <span style={{ color: '#778', fontStyle: 'italic' }}>
                    No network selected — pick one from the All networks list.
                  </span>
                )}
              </div>
              <TopologyView
                onSelect={setSelectedId}
                selectedId={selectedId}
                probedOkIds={probedOkIds}
                onProbeOk={markProbedOk}
                rootNodeId={selectedNetworkRoot ? selectedNetworkRoot.root_node_id : null}
                nodeIds={selectedNetworkRoot ? selectedNetworkRoot.node_ids : null}
                networkCanonical={selectedNetworkRoot ? selectedNetworkRoot.canonical_name : null}
                removedNodeIds={removedNodeIds}
                onFetch={setTopoLastFetch} />
            </>
          )}
          {tab === 'rendezvous' && <RendezvousPanel />}
          {tab === 'apps' && <AppsPanel onOpenNode={handleOpenAppNode} />}
          {tab === 'tasks' && <ApprovalsPanel />}
          {tab === 'permissions' && <PermissionsPanel />}
          {tab === 'users' && <UsersPanel />}
          {tab === 'jobs' && <JobsPanel />}
        </div>
        {tab === 'topology' && (
          // Own stacking context so the station panel overlays the topology canvas.
          <div style={{ position: 'relative', zIndex: 5, display: 'flex' }}>
          <div ref={setStationPanelHost}
               style={{ position: 'absolute', top: 0, bottom: 0, right: '100%', width: 0 }} />
          <div style={{ width: '360px', borderLeft: '1px solid #ddd', overflow: 'auto', padding: '16px', background: '#fff', position: 'relative', zIndex: 1 }}>
            <NodeDetail
              stationPanelHost={stationPanelHost}
              nodeId={selectedId}
              networkId={selectedNetworkRoot ? selectedNetworkRoot.id : null}
              networkCanonical={selectedNetworkRoot ? selectedNetworkRoot.canonical_name : null}
              onProbeOk={markProbedOk}
              probedOkIds={probedOkIds}
              onDeleted={(deletedId) => { setSelectedId(null); markNodeRemoved(deletedId); }} />
          </div>
          </div>
        )}
      </div>
      {/* indirect-liveness slug: app-wide status bar pinned to the bottom of
          the viewport (last child of the height:100vh flex column, flex:0 0
          auto). Its own panel so the topology legend is always visible
          regardless of canvas scroll.
          admin-version-to-legend (inline): bar now renders on every tab so
          the version badge (right edge) stays visible app-wide. The legend
          text on the left still only appears on the topology tab. */}
      <div style={{
        flex: '0 0 auto', borderTop: '1px solid #e0e0e0', background: '#fff',
        padding: '6px 20px', fontSize: '0.75rem', color: '#888',
        display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12,
      }}>
        <span>
          {tab === 'topology' && (
            <>
              polling /api/topology every {POLL_INTERVAL_MS / 1000}s
              {topoLastFetch && ` · last update ${topoLastFetch.toLocaleTimeString()}`}
              {' · liveness via rendezvous session presence; amber = unconfirmed (rendezvous unreachable)'}
            </>
          )}
        </span>
        <span style={{ color: '#999', fontVariantNumeric: 'tabular-nums' }}>
          {version && `v${version}`}
        </span>
      </div>

      {/* Per-network page actions (banner hamburger). On success, return to the
          All-networks list (refreshed) — avoids re-syncing the banner/URL when a
          rename changes the network's name. */}
      {netRenameTarget && (
        <RenameNetworkModal
          network={netRenameTarget}
          onClose={() => setNetRenameTarget(null)}
          onSuccess={() => { setNetRenameTarget(null); setSelectedNetworkRoot(null); setSelectedId(null); setTab('networks'); fetchNetworks(); }} />
      )}
      {netDecommTarget && (
        <DecommissionNetworkModal
          network={netDecommTarget}
          onClose={() => setNetDecommTarget(null)}
          onSuccess={() => { setNetDecommTarget(null); setSelectedNetworkRoot(null); setSelectedId(null); setTab('networks'); fetchNetworks(); }} />
      )}
      {netPublicTarget && (
        <PublicHelpersModal
          network={netPublicTarget}
          onClose={() => setNetPublicTarget(null)}
          onSuccess={() => setNetPublicTarget(null)} />
      )}
    </div>
  );
}

function Tab({ label, active, onClick }) {
  return (
    <button onClick={onClick} style={{
      background: 'none',
      border: 'none',
      padding: '4px 10px',
      cursor: 'pointer',
      fontSize: '0.85rem',
      color: active ? '#000' : '#888',
      borderBottom: active ? '2px solid #000' : '2px solid transparent',
      marginBottom: -2,
    }}>{label}</button>
  );
}
